// lib/koi-pond-v3/behaviors/moonCloudDimming.ts
//
// T10.3 — moon cloud-dimming. No cloud is rendered; instead the moon's
// brightness eases down and back as if an unseen cloud drifted across it.
// Every 30–60s: dim 1.0 → 0.7 over 2s, hold 3–5s, brighten back over 2s.
//
// Returns a single 0.7–1.0 factor per frame; the scene applies it to the
// moon disc/halo/flare, the water's moon reflection, and the koi so they
// all darken together.

const DIM = 0.7;
const RAMP = 2; // seconds to dim / brighten
const HOLD_MIN = 3;
const HOLD_MAX = 5;

const rand = (a: number, b: number) => a + Math.random() * (b - a);
const smoothstep = (x: number) => {
  const t = Math.min(1, Math.max(0, x));
  return t * t * (3 - 2 * t);
};

export function makeMoonCloudDimming(fast = false) {
  const GAP_MIN = fast ? 6 : 30;
  const GAP_MAX = fast ? 9 : 60;
  type Phase = 'full' | 'dimming' | 'hold' | 'brighten';
  let phase: Phase = 'full';
  let phaseStart = -1;
  let phaseDur = rand(GAP_MIN, GAP_MAX);

  function update(t: number): number {
    if (phaseStart < 0) phaseStart = t;
    const e = t - phaseStart;
    switch (phase) {
      case 'full':
        if (e >= phaseDur) { phase = 'dimming'; phaseStart = t; phaseDur = RAMP; }
        return 1;
      case 'dimming':
        if (e >= RAMP) { phase = 'hold'; phaseStart = t; phaseDur = rand(HOLD_MIN, HOLD_MAX); return DIM; }
        return 1 - (1 - DIM) * smoothstep(e / RAMP);
      case 'hold':
        if (e >= phaseDur) { phase = 'brighten'; phaseStart = t; phaseDur = RAMP; }
        return DIM;
      case 'brighten':
        if (e >= RAMP) { phase = 'full'; phaseStart = t; phaseDur = rand(GAP_MIN, GAP_MAX); return 1; }
        return DIM + (1 - DIM) * smoothstep(e / RAMP);
    }
  }

  return { update };
}
